"use client";

import Cookies from "js-cookie";
import { UAParser } from "ua-parser-js-v2";
import { isbot } from "isbot";

import { BiqQueryPropertyType } from "~/services/api/services/types/EventsEnum";
import { VERCEL_DEPLOYMENT_ID } from "~/services/api/config";
import { AppConfig } from "~/utils/AppConfig";
import { STORAGE_KEYS } from "~/types/Storage";
import { COOKIES } from "~/types/Cookie";
import { getStorageItem } from "~/lib/storage/Storage";
import { getPagePath } from "~/utils/pagePath";

/**
 * How long events wait for the `initial_sitewide` response before going out
 * anyway. The server stamps `event_ts` on arrival, so waiting keeps the session
 * start first in BigQuery; a cold-started API answering in seconds must not
 * hold the rest of the visit hostage to a user who leaves meanwhile.
 */
export const SITEWIDE_GATE_TIMEOUT_MS = 1500;

let isSitewideGateOpen = false;
let resolveSitewideGate!: () => void;
const sitewideGate = new Promise<void>((resolve) => {
    resolveSitewideGate = resolve;
});

const openSitewideGate = () => {
    isSitewideGateOpen = true;
    resolveSitewideGate();
};

// Leaving or backgrounding the page is the last chance to send what is still
// queued: a closed tab drops it and a frozen one never runs its timers again.
if (typeof window !== "undefined") {
    window.addEventListener("pagehide", openSitewideGate);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") openSitewideGate();
    });
}

let builtEventCount = 0;

export function getWebEventObject(args) {
    args.meta_data = args.meta_data || {}
    if (typeof args.meta_data === "string" || args.meta_data instanceof String) {
        try {
            args.meta_data = JSON.parse(args.meta_data);
        } catch (error) {
            console.error(error);
        }
    }
 
    args.meta_data.deploymentId = VERCEL_DEPLOYMENT_ID;
    args.meta_data.vcrr = Cookies.get(COOKIES.VCRR);
    // `event_ts` is stamped by the server on arrival, so a slow request reorders
    // it; these keep the order in which this page sent its events.
    args.meta_data.client_ts = new Date().toISOString();
    args.meta_data.event_seq = ++builtEventCount;

    const parser = new UAParser(navigator.userAgent);

    const cookie = {ga: Cookies.get("_ga"), fbp: Cookies.get("_fbp"), fbc: Cookies.get("_fbc"), gclid: Cookies.get(COOKIES.GCLID), wbraid: Cookies.get(COOKIES.WBRAID), gbraid: Cookies.get(COOKIES.GBRAID)};

    const userAgent_json = {
        resolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
        deviceType: parser.getDevice().type ? parser.getDevice().type : "desktop",
        operating_system: `${parser.getOS().name} ${parser.getOS().version}`,
        ...parser.getResult(),
    }

    const vercelCanaryId = Cookies.get(COOKIES.VERCEL_CANARY_ID);
    const environment = vercelCanaryId && vercelCanaryId === VERCEL_DEPLOYMENT_ID ? "rc" : AppConfig.isProduction ? "prod" : "qa";

    return {
        type: BiqQueryPropertyType.WEB_EVENT,
        properties: {
            brand: "InnerBalance",
            referral_page: document.referrer,
            userAgent: window.navigator.userAgent,
            userAgent_json: JSON.stringify(userAgent_json),
            environment: environment,
            ip_address_client: Cookies.get(COOKIES.IP_ADDRESS),
            deviceType: parser.getDevice().type ? parser.getDevice().type : "desktop",
            operating_system: `${parser.getOS().name} ${parser.getOS().version}`,
            cookie,
            doc_location: getStorageItem(STORAGE_KEYS.DOC_LOCATION),
            event_name: args.event_name,
            event_type: args.event_type,
            page_path: args.page_path || getPagePath(),
            widget: args.widget,
            country_code: Cookies.get(COOKIES.COUNTRY_CODE),
            click_text: args.meta_data?.click_text || "",
            meta_data: JSON.stringify(args.meta_data),
            href: args.href || "",
            user_id: args.meta_data.user_id
        },
    }
}

export async function sendWebEvent(sendBigQueryEvent, args, callback?) {
    if (!getStorageItem(STORAGE_KEYS.IS_ADMIN_LOGIN) && typeof window !== "undefined" && !isbot(navigator.userAgent)) {
        const eventName = args.event_name;
        args.page_path = args.page_path || getPagePath()

        const isSitewide = eventName === "initial_sitewide";

        if (isSitewide || isSitewideGateOpen) {
            const gateTimeout = isSitewide
                ? setTimeout(openSitewideGate, SITEWIDE_GATE_TIMEOUT_MS)
                : undefined;
            let webEvent: ReturnType<typeof getWebEventObject>;
            try {
                webEvent = getWebEventObject(args);
            } catch (error) {
                // Deferred events wait on this gate; left closed, they never go out.
                if (isSitewide) openSitewideGate();
                throw error;
            }
            // Callers fire and forget, so a rejection here surfaces as an
            // unattributed global "Load failed" whenever the network drops.
            // A lost analytics event is not an error worth reporting.
            try {
                await sendBigQueryEvent(webEvent);
            } catch {
                // Intentionally ignored.
            }
            if (isSitewide) {
                clearTimeout(gateTimeout);
                openSitewideGate();
            }
        } else {
            void sitewideGate.then(() => sendWebEvent(sendBigQueryEvent, args));
        }
    }

    callback?.();
    return null;
}